import React, { useMemo } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { PartialDateRange } from 'libs/end-to-end/src/date-range';
import { DateRange as ReactDateRange } from 'libs/end-to-end/src/date-range';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { S2DateRangePicker } from 'apps/sigma2-ui/src/components/DateRangePicker';

export interface CopilotViewFiltersProps {
  departments: string[];
  roles: string[];
  locations: string[];
  selectedDepartments: string[];
  selectedRoles: string[];
  selectedLocations: string[];
  setSelectedDepartments: Setter<string[]>;
  setSelectedRoles: Setter<string[]>;
  setSelectedLocations: Setter<string[]>;
  selectedStartDate: Date | undefined;
  selectedEndDate: Date | undefined;
  setSelectedStartDate: Setter<Date | undefined>;
  setSelectedEndDate: Setter<Date | undefined>;
  onApplyFilters: () => void;
  canApplyFilters: boolean;
  focusArea: FocusArea;
}

export const CopilotViewFilters = (props: CopilotViewFiltersProps) => {
  const language = useLanguage();

  const selectedDates: PartialDateRange = useMemo(
    () => ({
      start: props.selectedStartDate ?? null,
      end: props.selectedEndDate ?? null,
    }),
    [props.selectedStartDate, props.selectedEndDate],
  );

  const onDateSelect = (selected?: PartialDateRange): void => {
    props.setSelectedStartDate(selected?.start ?? undefined);
    props.setSelectedEndDate(selected?.end ?? undefined);
  };

  const widthClass = props.focusArea === 'Hiring' ? 'w-40' : 'w-52';

  const roleBox = useMemo(() => {
    return props.focusArea === 'Hiring' ? (
      <SelectBoxWithSearch
        type="multi"
        list={props.roles.map((role) => ({
          optionLabel: role,
          optionValue: role,
        }))}
        label={language.hiringCopilotFilterRole()}
        selected={props.selectedRoles}
        setSelected={props.setSelectedRoles}
        widthClass={widthClass}
      />
    ) : (
      <SelectBoxWithSearch
        type="single"
        list={props.roles.map((role) => ({
          optionLabel: role,
          optionValue: role,
        }))}
        label={language.hiringCopilotFilterRole()}
        selected={
          props.selectedRoles.length > 0 && props.selectedRoles[0]
            ? props.selectedRoles[0]
            : ''
        }
        setSelected={(newRole) => {
          props.setSelectedRoles(newRole ? [newRole] : []);
        }}
        widthClass={widthClass}
      />
    );
  }, [language, props, widthClass]);

  return (
    <div className="flex gap-2 justify-around bg-[#FAFAFA] p-4 shadow-2px rounded-md items-center">
      {props.focusArea === 'Hiring' ? (
        <SelectBoxWithSearch
          type="multi"
          list={props.departments.map((dept) => ({
            optionLabel: dept,
            optionValue: dept,
          }))}
          label={language.hiringCopilotFilterDepartment()}
          selected={props.selectedDepartments}
          setSelected={props.setSelectedDepartments}
          widthClass={widthClass}
        />
      ) : (
        <SelectBoxWithSearch
          type="single"
          list={props.departments.map((dept) => ({
            optionLabel: dept,
            optionValue: dept,
          }))}
          label={language.hiringCopilotFilterDepartment()}
          selected={
            props.selectedDepartments.length === 0
              ? ''
              : props.selectedDepartments[0]
          }
          setSelected={(dept) => {
            props.setSelectedDepartments(!dept ? [] : [dept]);
          }}
          widthClass={widthClass}
        />
      )}
      {roleBox}
      <SelectBoxWithSearch
        type="multi"
        list={props.locations.map((location) => ({
          optionLabel: location,
          optionValue: location,
        }))}
        label={language.hiringCopilotFilterLocation()}
        selected={props.selectedLocations}
        setSelected={props.setSelectedLocations}
        widthClass={widthClass}
      />

      {props.focusArea === 'Hiring' ? (
        <S2DateRangePicker
          value={selectedDates}
          onChange={onDateSelect}
          label={language.applicationDateLabel()}
          outline={true}
        />
      ) : (
        <></>
      )}

      <LegacyButton
        data-qa="calculate-btn"
        disabled={!props.canApplyFilters}
        variant={LegacyButtonVariant.APPLY_BUTTON}
        onClick={props.onApplyFilters}
        className="font-sans relative cursor-default w-32 text-center justify-center"
      >
        Apply
      </LegacyButton>
    </div>
  );
};
